import { ADMIN_APPS } from "@/apps/registry"
import { Badge, Input, Prompt, RadioGroup, Text, toast } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Link } from "react-router-dom"
import { ErrorState, Section } from "@/components/ui"
import { formatDate } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { errorMessage, sdk } from "@/lib/sdk"
import { SettingsTitle } from "./settings-general"

type App = { id: string; name: string; description: string; available: boolean; enabled: boolean; updated_at: string | null; in_use?: { count: number; label: string } | null }

const OFF_EFFECT: Record<string, string> = {
  reviews: "Reviews disappears from the sidebar, the storefront stops showing reviews and the review form, and review request emails stop. Every review, photo and setting is kept.",
  upsells: "Cart & Checkout disappears from the sidebar. The cart drawer stops showing offer cards, Shipping Protection and the reservation bar; checkout stops showing offers, the timer, the checkout image and reviews; product pages drop their promo bar. Offer items already in carts can't be bought at the offer price. Every offer and setting is kept.",
  subscriptions:
    "Subscriptions disappears from the sidebar, the storefront sells one-time only and no renewals are charged while it's off. When you turn it back on, renewals that fell due in between are not charged automatically: they wait in Upcoming charges → Overdue for you to charge or reschedule.",
}

/**
 * Settings → Apps: one Enabled / Disabled choice per Terify app. There is no
 * uninstall: switching off hides the app everywhere (the admin doesn't even
 * download its screens) and keeps its data.
 */
export function AppsSettingsPage() {
  const qc = useQueryClient()
  const writable = useCan()("settings:write")
  const list = useQuery({ queryKey: ["settings", "apps"], queryFn: () => sdk.client.fetch<{ apps: App[] }>("/admin/terify/settings/apps") })
  const set = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) => sdk.client.fetch<{ app: App }>(`/admin/terify/settings/apps/${v.id}`, { method: "POST", body: { enabled: v.enabled } }),
    onSuccess: async (r) => {
      await Promise.all([qc.invalidateQueries({ queryKey: ["settings", "apps"] }), qc.invalidateQueries({ queryKey: ["me"] })])
      toast.success(`${r.app.name} ${r.app.enabled ? "turned on" : "turned off"}`)
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const [confirmOff, setConfirmOff] = useState<App | null>(null)
  const [typed, setTyped] = useState("")
  const needsTyping = !!confirmOff?.in_use
  if (list.error) return <ErrorState error={list.error} />
  const choose = (a: App, v: string) => {
    const enabled = v === "enabled"
    if (enabled === a.enabled) return
    if (!enabled) {
      setTyped("")
      setConfirmOff(a)
    }
    else set.mutate({ id: a.id, enabled: true })
  }
  return (
    <div className="flex flex-col gap-4 pb-24">
      <SettingsTitle title="Apps" />
      {!list.data ? (
        <Text size="small">Loading…</Text>
      ) : (
        list.data.apps.map((a) => (
          <Section
            key={a.id}
            title={
              <span className="flex items-center gap-x-2">
                {a.name}
                {!a.available ? <Badge size="2xsmall">Coming soon</Badge> : a.enabled ? <Badge size="2xsmall" color="green">On</Badge> : <Badge size="2xsmall">Off</Badge>}
              </span>
            }
            actions={a.enabled && ADMIN_APPS.some((x) => x.id === a.id) ? <Link to={`/apps/${a.id}`} className="text-[13px] text-ui-fg-interactive hover:underline">Open</Link> : undefined}
          >
            <div className="flex flex-col gap-3 px-4 py-3">
              <Text size="small" className="text-ui-fg-subtle">{a.description}</Text>
              <RadioGroup value={a.enabled ? "enabled" : "disabled"} onValueChange={(v) => choose(a, v)} disabled={!writable || !a.available || set.isPending} className="flex flex-col gap-y-2" aria-label={`${a.name} app`}>
                {[
                  ["enabled", "Enabled", "Shown in the sidebar and working on the storefront."],
                  ["disabled", "Disabled", "Hidden everywhere. Nothing is deleted."],
                ].map(([val, label, hint]) => (
                  <label key={val} className="flex items-start gap-x-2">
                    <RadioGroup.Item value={val} className="mt-0.5" />
                    <div>
                      <Text size="small">{label}</Text>
                      <Text size="xsmall" className="text-ui-fg-subtle">{hint}</Text>
                    </div>
                  </label>
                ))}
              </RadioGroup>
              {a.updated_at ? <Text size="xsmall" className="text-ui-fg-muted">Last changed {formatDate(a.updated_at)}</Text> : null}
            </div>
          </Section>
        ))
      )}
      <Prompt open={!!confirmOff}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Turn off {confirmOff?.name}?</Prompt.Title>
            <Prompt.Description>{confirmOff ? OFF_EFFECT[confirmOff.id] ?? "The app is hidden everywhere. Its data is kept." : ""}</Prompt.Description>
          </Prompt.Header>
          {needsTyping && (
            <div className="flex flex-col gap-y-2 px-6 pb-4">
              <Text size="small" weight="plus" className="text-ui-fg-error">{confirmOff?.in_use?.label}.</Text>
              <Text size="xsmall" className="text-ui-fg-subtle">Type DISABLE to confirm.</Text>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DISABLE" aria-label="Type DISABLE to confirm" autoComplete="off" />
            </div>
          )}
          <Prompt.Footer>
            <Prompt.Cancel onClick={() => setConfirmOff(null)}>Cancel</Prompt.Cancel>
            <Prompt.Action
              disabled={needsTyping && typed.trim() !== "DISABLE"}
              onClick={() => {
                if (confirmOff) set.mutate({ id: confirmOff.id, enabled: false })
                setConfirmOff(null)
              }}
            >
              Turn off
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </div>
  )
}
